import { generateSitemapData, generateSitemapXML, SitemapEntry } from '../utils/seo'

export class SitemapService {
  private static instance: SitemapService
  private sitemapData: SitemapEntry[] = []
  private lastGenerated: Date | null = null
  
  private constructor() {}
  
  public static getInstance(): SitemapService {
    if (!SitemapService.instance) {
      SitemapService.instance = new SitemapService()
    }
    return SitemapService.instance
  }
  
  // Generate fresh sitemap data
  public generateSitemap(): SitemapEntry[] {
    this.sitemapData = generateSitemapData()
    this.lastGenerated = new Date()
    return this.sitemapData
  }
  
  // Get cached sitemap data or generate if stale
  public getSitemapData(maxAge: number = 3600000): SitemapEntry[] { // 1 hour default
    const now = new Date()
    const isStale = !this.lastGenerated || 
      (now.getTime() - this.lastGenerated.getTime()) > maxAge
    
    if (isStale || this.sitemapData.length === 0) {
      return this.generateSitemap()
    }
    
    return this.sitemapData
  }
  
  // Generate XML sitemap string
  public generateSitemapXML(): string {
    const data = this.getSitemapData()
    return generateSitemapXML(data)
  }
  
  // Add custom URL to sitemap
  public addCustomUrl(entry: SitemapEntry): void {
    const existingIndex = this.sitemapData.findIndex(item => item.url === entry.url)
    if (existingIndex >= 0) {
      this.sitemapData[existingIndex] = entry
    } else {
      this.sitemapData.push(entry)
    }
  }
  
  // Remove URL from sitemap
  public removeUrl(url: string): void {
    this.sitemapData = this.sitemapData.filter(item => item.url !== url)
  }
  
  // Get sitemap as downloadable blob
  public getSitemapBlob(): Blob {
    const xml = this.generateSitemapXML()
    return new Blob([xml], { type: 'application/xml' })
  }
  
  // Download sitemap file
  public downloadSitemap(): void {
    const blob = this.getSitemapBlob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'sitemap.xml'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}

// Export singleton instance
export const sitemapService = SitemapService.getInstance()

// Utility function to register sitemap endpoint
export const registerSitemapEndpoint = (): void => {
  // This would typically be handled by the backend
  // For client-side generation, we can expose it via a route or API endpoint
  if (typeof window !== 'undefined') {
    // Add sitemap data to window for debugging
    (window as any).__MERNAKI_SITEMAP__ = {
      getData: () => sitemapService.getSitemapData(),
      getXML: () => sitemapService.generateSitemapXML(),
      download: () => sitemapService.downloadSitemap()
    }
  }
}